/**
 * MODIFIED: 2025-01-29 - refactor/task-1.5-dashboard-performance
 *
 * ARCHITECTURE: API Architecture
 * PURPOSE: Authentication operations including login, logout, token management, and password reset
 *
 * NEW PATTERNS IMPLEMENTED:
 * - ApiClient Pattern: Centralized HTTP handler with generic types
 * - Error Handling: Comprehensive ApiError parsing with field-level errors
 * - Token Refresh: Automatic retry on 401 with exponential backoff
 * - Safe Variants: Tuple return [data | null, error | null]
 * - Path Builder: Functional endpoint construction via API_CONFIG
 *
 * KEY FEATURES:
 * - Form-based login with OAuth2-style credentials
 * - Token rotation with refresh token management
 * - Session storage with sessionStorage (auto-cleanup on tab close)
 * - Permission extraction from role data
 * - Password reset workflow (request, validate, confirm)
 * - Account locking detection and handling
 *
 * RELATED FILES:
 * - src/lib/services/api/client.ts
 * - src/lib/config/apiConfig.ts
 * - src/lib/state/auth.state.svelte.ts
 * - src/lib/services/auth/TokenService.ts
 */

//src/lib/services/api/auth.api.ts
import { apiClient } from './client';
import { API_CONFIG } from '$lib/config/apiConfig';
import type {
	LoginCredentials,
	LoginResponse,
	LoginResult,
	TokenRefreshResponse
} from '$lib/types/core/auth.types';
import { ApiError } from '$lib/types/api/error.types';
import type { RoleInfo, User, UserPermissions, UserProfile } from '$lib/types/core/user.types';
import type { MeApiResponse } from '$lib/types/api';
import { setAuthenticatedFromLogin } from '$lib/state/auth.state.svelte';
import { trackStage, endStage } from '$lib/utils/performanceTracker';
import { createLogger } from '$lib/utils/logger';
import { secureStorage } from '$lib/utils/secure-storage';

const logger = createLogger('auth.api');

/**
 * Authentication API service for user authentication operations
 * This service interacts directly with the API endpoints.
 * It saves data to sessionStorage but does NOT directly update Svelte stores.
 */
export class AuthApi {
	/**
	 * Login with username and password
	 * @param credentials User credentials
	 * @returns Promise with login result
	 */
	async login(credentials: LoginCredentials): Promise<LoginResult> {
		try {
			// Security Note: Password logging removed to comply with OWASP, GDPR, SOC-2, and PCI-DSS requirements.
			// Credentials must never be logged in plain text or any identifiable form.

			// Create form data as required by the backend API
			const formData = new URLSearchParams();
			formData.append('username', credentials.email);
			formData.append('password', credentials.password);

			logger.info('Login request initiated', { email: credentials.email });

			// Track login API call performance
			trackStage('auth:login:api', { username: credentials.email });

			// Using fetch directly to handle form-urlencoded content type
			const response = await fetch(API_CONFIG.AUTH.LOGIN(), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded'
				},
				body: formData.toString(),
				credentials: 'include' // Include cookies if needed for session management
			});

			const loginApiTime = endStage('auth:login:api', { status: response.status });
			logger.info('Login API completed', {
				email: credentials.email,
				status: response.status,
				durationMs: loginApiTime
			});

			// If not successful, parse error response
			if (!response.ok) {
				const errorText = await response.text();
				let errorData;
				try {
					errorData = JSON.parse(errorText); // Try to parse JSON error detail
				} catch (e) {
					// If not JSON, use the raw text
					throw new Error(`Login failed: ${response.status} - ${errorText}`);
				}
				// Throw structured API error if JSON parsing succeeded
				throw ApiError.fromResponse(response, errorData);
			}

			// Parse successful JSON response
			const data: LoginResponse = await response.json();
			logger.debug('Login successful, saving session');

			// Store session data in sessionStorage (token, user details, etc.)
			this.saveSession(data);

			// Update auth state with ALL fields from login response
			setAuthenticatedFromLogin(data);

			// Return success without the actual data (AuthService will handle state)
			return { success: true };
		} catch (error) {
			logger.error('Login failed', { error, email: credentials.email });
			// Handle structured API errors
			if (error instanceof ApiError) {
				return {
					success: false,
					error: error.toUserFriendlyMessage(), // User-friendly message
					apiError: error // Pass the full error object for potential field errors
				};
			}
			// Handle generic errors
			return {
				success: false,
				error: error instanceof Error ? error.message : 'An unexpected error occurred during login'
			};
		}
	}

	/**
	 * Refresh the access token using the refresh token stored in sessionStorage.
	 * Updates sessionStorage with the new token details upon success.
	 * @returns Promise resolving to true if refresh was successful, false otherwise.
	 */
	async refreshToken(): Promise<boolean> {
		if (typeof window === 'undefined') return false;

		const refreshToken = secureStorage.getRefreshToken();
		if (!refreshToken) {
			logger.debug('No refresh token available');
			return false;
		}

		logger.debug('Attempting token refresh');
		try {
			const response = await apiClient.post<TokenRefreshResponse>(API_CONFIG.AUTH.REFRESH(), {
				refresh_token: refreshToken
			});

			// Check if response contains the new tokens
			if (response && response.access_token) {
				// Update access token
				secureStorage.setToken(response.access_token);
				secureStorage.setTokenType(response.token_type || 'bearer');

				// Update WebSocket authentication with refreshed token (Phase 3 fix)
				try {
					import('$lib/services/websocket/websocket.actions.svelte').then(({ updateAuth }) => {
						updateAuth(response.access_token, response.token_type || 'Bearer');
						logger.debug('WebSocket auth updated after token refresh');
					});
				} catch (error) {
					logger.error('Failed to update WebSocket auth during refresh', { error });
				}

				// IMPORTANT: Store the new refresh token (implementing token rotation)
				const rotated = !!response.refresh_token;
				if (rotated) {
					secureStorage.setRefreshToken(response.refresh_token);
				}

				// Determine and save token expiration timestamp
				let expiresAt: number;

				// Try to get expiry from the server-provided field first
				if (response.access_token_expires_at) {
					try {
						// Parse ISO date or timestamp
						const expiryDate = new Date(response.access_token_expires_at);
						expiresAt = expiryDate.getTime();
						// FIXED: Store access token expiry in secure storage
						secureStorage.setTokenExpiry(new Date(expiresAt));
					} catch (e) {
						logger.error('Failed to parse access token expiry', { error: e });
					}
				}

				// Also save refresh token expiry if provided
				if (response.refresh_token_expires_at) {
					try {
						const refreshExpiryDate = new Date(response.refresh_token_expires_at);
					} catch (e) {
						logger.error('Failed to parse refresh token expiry', { error: e });
					}
				}

				logger.info('Token refresh successful', { tokenRotated: rotated });
				return true;
			} else {
				logger.warn('Token refresh failed - invalid API response');
				return false;
			}
		} catch (error) {
			logger.error('Token refresh failed', { error });
			// Don't clear tokens here - let the AuthService handle retry logic
			return false;
		}
	}

	/**
	 * Attempt to logout the user via API call.
	 * Also clears local session data regardless of API call success.
	 */
	async logout(): Promise<void> {
		logger.info('Logout initiated');

		// Get the current token before we start clearing anything
		const currentToken = secureStorage.getToken();
		const tokenType = secureStorage.getTokenType() || 'Bearer';

		try {
			if (currentToken) {
				// Make a direct fetch call with the token to avoid race conditions
				// This bypasses our normal client which might have other side effects
				try {
					const response = await fetch(API_CONFIG.AUTH.LOGOUT(), {
						method: 'POST',
						headers: {
							Authorization: `${tokenType} ${currentToken}`,
							'Content-Type': 'application/json'
						},
						body: JSON.stringify({})
					});

					if (response.ok) {
						logger.debug('API logout call successful');
					} else {
						logger.warn('API logout failed', { status: response.status });
					}
				} catch (error) {
					logger.error('API logout call failed', { error });
				}
			} else {
				logger.debug('No token found, skipping API logout call');
			}
		} finally {
			// Always clear local session data regardless of API success
			this.clearSession();
			logger.info('Session cleared');
		}
	}

	/**
	 * Get the current user information from the API (/users/me endpoint).
	 * Changed to return UserProfile instead of User to align with state
	 * @returns Promise resolving to the UserProfile object or null
	 */
	async getCurrentUser(): Promise<UserProfile | null> {
		logger.debug('Fetching current user from API');
		try {
			// Ensure we have a valid token before calling /me
			if (!this.isAuthenticated()) {
				logger.debug('Not authenticated, cannot fetch current user');
				return null;
			}

			// Get base user data
			const user = await apiClient.get<MeApiResponse>(API_CONFIG.AUTH.ME());

			if (!user) {
				return null;
			}

			// Transform to UserProfile
			const userProfile: UserProfile = {
				// Include all User fields with defaults for missing ones
				id: user.id,
				email: user.email,
				name: user.name,
				organization_id: user.organization_id,
				is_active: true, // Add default for required field
				is_company_user: user.is_company_user,
				user_level: user.user_level,
				primary_department_id: null, // Add default for required field
				last_login: user.last_login || null,
				login_count: 0, // Add default for required field
				failed_login_attempts: 0, // Add default for required field
				last_failed_login: null, // Add default for missing field
				last_active: null, // Add default for missing field
				is_locked: false, // Add default for missing field
				locked_at: null, // Add default for missing field
				locked_reason: null, // Add default for missing field
				require_password_change: false, // Add default for missing field
				is_system_user: false, // Add default for missing field
				mfa_enabled: false, // Add default for missing field
				permission_sync_version: 0, // Add default for missing field
				last_permission_sync: null, // Add default for missing field
				created_at: new Date().toISOString(), // Add default for missing field
				updated_at: new Date().toISOString(), // Add default for missing field

				// UserProfile specific fields - try to get from API response first, then fallback to stored data
				organization_name:
					user.organization_name || this.getStoredUserData()?.organization_name || '',
				organization_code:
					user.organization_code || this.getStoredUserData()?.organization_code || '',
				preferences: user.preferences || {},
				notification_settings: user.notification_settings || {},
				ui_settings: user.ui_settings || {},
				roles: user.roles || [],
				departments: user.departments || []
			};

			// Validate organization data
			if (!userProfile.organization_code) {
				logger.warn('User profile from /me endpoint missing organization_code', {
					email: userProfile.email
				});
			} else {
				logger.debug('Current user fetched successfully', {
					email: userProfile.email,
					organizationCode: userProfile.organization_code
				});
			}

			return userProfile;
		} catch (error) {
			logger.error('Failed to get current user from API', { error });
			// If error is 401/403, token is invalid, clear session
			if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
				logger.info('Clearing session due to auth error');
				this.clearSession();
			}
			return null; // Return null on any error
		}
	}

	/**
	 * Checks authentication status by validating the token and potentially refreshing it.
	 * Primarily used by AuthService during initialization.
	 * @returns Promise resolving to true if authenticated and validated, false otherwise.
	 */
	async checkAuth(): Promise<boolean> {
		if (typeof window === 'undefined') return false;

		logger.debug('Performing auth check');

		// Check local token validity first
		if (!this.hasToken() || this.isTokenExpired()) {
			logger.debug('Local token missing or expired, attempting refresh');
			// Try to refresh the token if expired or missing
			const refreshed = await this.refreshToken();
			if (!refreshed) {
				logger.warn('Auth check failed - token refresh failed');
				return false; // Failed if refresh failed
			}
			logger.debug('Token refreshed, continuing auth check');
		}

		// After potentially refreshing, verify the current token by fetching user data
		// This confirms the token (new or existing) is valid on the server.
		try {
			const user = await apiClient.get<User>(API_CONFIG.AUTH.ME());
			const isAuthenticated = !!user;
			logger.info('Auth check completed', { isAuthenticated });
			return isAuthenticated;
		} catch (error) {
			logger.error('Auth check failed during /me call', { error });
			// If error is 401/403, token is invalid, clear session
			if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
				logger.info('Clearing session due to auth error during check');
				this.clearSession();
			}
			return false;
		}
	}

	/**
	 * Checks if a token exists locally and is not expired according to sessionStorage.
	 * This is a quick check and does NOT guarantee server-side validity.
	 * @returns Boolean indicating local token presence and non-expiry.
	 */
	public isAuthenticated(): boolean {
		// Check if a token exists and its stored expiry time is in the future
		return this.hasToken() && !this.isTokenExpired();
	}

	/**
	 * Saves session data (tokens, user info, permissions) to sessionStorage after successful login.
	 * @param data The raw response data from the login API endpoint.
	 */
	private saveSession(data: LoginResponse): void {
		if (typeof window === 'undefined' || !data.access_token) {
			return;
		}
		logger.debug('Saving session data to secure storage');

		// Save tokens
		secureStorage.setToken(data.access_token);
		secureStorage.setRefreshToken(data.refresh_token);
		secureStorage.setTokenType(data.token_type || 'bearer');

		// Update WebSocket authentication with new token (Phase 3 fix)
		try {
			// Dynamically import to avoid circular dependencies
			import('$lib/services/websocket/websocket.actions.svelte').then(({ updateAuth }) => {
				updateAuth(data.access_token, data.token_type || 'Bearer');
				logger.debug('WebSocket auth updated after login');
			});
		} catch (error) {
			logger.error('Failed to update WebSocket auth', { error });
		}

		// Save structured user data as UserProfile
		const userProfile: UserProfile = {
			// Base User properties with defaults
			id: data.user_id || '',
			email: data.email || '',
			name: data.name || '',
			organization_id: data.organization_id || '',
			is_active: true, // Default value
			is_company_user: data.is_company_user ?? false,
			user_level: data.user_level,
			primary_department_id: null, // Default value
			last_login: data.last_login || null,
			login_count: 0, // Default value
			failed_login_attempts: 0, // Default value
			last_failed_login: null, // Default value
			last_active: null, // Default value
			is_locked: false, // Default value
			locked_at: null, // Default value
			locked_reason: null, // Default value
			require_password_change: false, // Default value
			is_system_user: false, // Default value
			mfa_enabled: false, // Default value
			permission_sync_version: 0, // Default value
			last_permission_sync: null, // Default value
			created_at: new Date().toISOString(), // Default value
			updated_at: new Date().toISOString(), // Default value

			// UserProfile extended properties
			organization_name: data.organization_name || '',
			organization_code: data.organization_code || '',
			preferences: {}, // Default empty object
			notification_settings: {}, // Default empty object
			ui_settings: {}, // Default empty object
			roles: data.roles || [],
			departments: data.departments || []
		};

		secureStorage.setUserData(JSON.stringify(userProfile));

		// Process and save derived permissions
		const permissions = this.extractPermissionsFromRoles(data.roles);
		secureStorage.setPermissions(JSON.stringify(permissions));

		// Determine and save token expiration timestamp
		let expiresAt: number;

		// Try to get expiry from the server-provided field first
		if (data.access_token_expires_at) {
			try {
				// Parse ISO date or timestamp
				const expiryDate = new Date(data.access_token_expires_at);
				expiresAt = expiryDate.getTime();
				// FIXED: Store access token expiry in secure storage
				secureStorage.setTokenExpiry(new Date(expiresAt));
			} catch (e) {
				logger.error('Failed to parse access token expiry', { error: e });
			}
		}

		// Also save refresh token expiry if provided
		if (data.refresh_token_expires_at) {
			try {
				const refreshExpiryDate = new Date(data.refresh_token_expires_at);
			} catch (e) {
				logger.error('Failed to parse refresh token expiry', { error: e });
			}
		}

		logger.debug('Session data saved successfully');
	}

	/**
	 * Add restoreSessionFromStorage method that's called by AuthService
	 * Validates user data and permissions from sessionStorage
	 */
	public restoreSessionFromStorage(): void {
		try {
			if (typeof window === 'undefined') return;

			const userData = secureStorage.getUserData();
			const permissionsData = secureStorage.getPermissions();

			if (userData) {
				logger.debug('User data exists in secure storage');
				// FIX: getUserData() and getPermissions() already return parsed objects
				// No need to call JSON.parse() again - that causes "[object Object] is not valid JSON" error
				// The data is already validated by the fact that getUserData() successfully returned it
				if (permissionsData) {
					logger.debug('Session data validated in secure storage', {
						hasUser: true,
						hasPermissions: true
					});
				} else {
					logger.debug('Session data validated in secure storage', {
						hasUser: true,
						hasPermissions: false
					});
				}
			} else {
				logger.debug('No user data found in secure storage');
			}
		} catch (error) {
			logger.error('Failed to check session from storage', {
				error: error instanceof Error ? error.message : String(error)
			});
		}
	}

	private extractExpiryFromToken(token: string): number {
		let expiresAt = Date.now() + 24 * 60 * 60 * 1000; // Default: 24 hours
		try {
			const tokenParts = token.split('.');
			if (tokenParts.length === 3) {
				const payload = JSON.parse(atob(tokenParts[1].replace(/-/g, '+').replace(/_/g, '/'))); // Base64 URL decode
				if (payload.exp && typeof payload.exp === 'number') {
					expiresAt = payload.exp * 1000; // Use 'exp' claim (in seconds)
				}
			}
		} catch (e) {
			logger.error('Failed to parse token expiry', { error: e });
		}

		return expiresAt;
	}

	/**
	 * Extract permissions from roles data
	 */
	private extractPermissionsFromRoles(roles: RoleInfo[]): UserPermissions {
		// Initialize the permissions structure with metadata field
		const permissions: UserPermissions = {
			dashboards: {},
			features: {},
			direct_permissions: [],
			metadata: {} // Add this field that was missing
		};

		// Default dashboard visibility (can be overridden by roles)
		permissions.dashboards['HOME_DASHBOARD'] = { visible: true, components: {} };

		if (!roles || !Array.isArray(roles) || roles.length === 0) {
			return permissions; // Return default if no roles
		}

		// Process permissions from each role
		for (const role of roles) {
			if (!role.permissions || typeof role.permissions !== 'object') continue;

			// Assuming role.permissions is { resource: [action1, action2], ... }
			Object.entries(role.permissions).forEach(([resource, actions]) => {
				if (Array.isArray(actions)) {
					// Add to direct permissions list
					permissions.direct_permissions.push({ resource, actions });

					// Dashboard visibility
					if (this.resourceIsDashboard(resource) && actions.includes('VIEW')) {
						const dashboardKey = this.mapResourceToDashboard(resource);
						if (dashboardKey) {
							permissions.dashboards[dashboardKey] = {
								visible: true,
								components: permissions.dashboards[dashboardKey]?.components || {}
							};
						}
					}

					// Feature Flags
					if (resource === 'USER' && actions.includes('MANAGE')) {
						permissions.features.can_manage_users = true;
					}
					if (resource === 'SYSTEM' && actions.includes('VIEW_LOGS')) {
						permissions.features.can_view_audit_logs = true;
					}
				}
			});
		}

		return permissions;
	}

	/**
	 * Helper to check if a permission resource string relates to a known dashboard concept.
	 */
	private resourceIsDashboard(resource: string): boolean {
		const dashboardResources = [
			'DASHBOARD',
			'ADMIN',
			'PROJECT',
			'ANALYTICS',
			'HOME',
			'ALERTS',
			'REPORTS'
		];
		// Use uppercase comparison for case-insensitivity
		return dashboardResources.includes(resource.toUpperCase());
	}

	/**
	 * Helper to map a permission resource string to a predefined dashboard key.
	 */
	private mapResourceToDashboard(resource: string): string | null {
		const upperResource = resource.toUpperCase();
		const resourceToDashboard: Record<string, string> = {
			DASHBOARD: 'HOME_DASHBOARD',
			ADMIN: 'ADMIN_DASHBOARD',
			PROJECT: 'PROJECT_DASHBOARD',
			ANALYTICS: 'ANALYTICS_DASHBOARD',
			HOME: 'HOME_DASHBOARD',
			ALERTS: 'ALERTS_DASHBOARD',
			REPORTS: 'REPORTS_DASHBOARD'
		};
		return resourceToDashboard[upperResource] || null;
	}

	/**
	 * Clears all authentication-related items from secure storage.
	 */
	private clearSession(): void {
		if (typeof window === 'undefined') return;
		logger.debug('Clearing auth data from secure storage');
		secureStorage.clearAll();
	}

	/**
	 * Checks if an access token exists in secure storage.
	 */
	private hasToken(): boolean {
		if (typeof window === 'undefined') return false;
		return !!secureStorage.getToken();
	}

	/**
	 * Get stored organization code from secure storage
	 */
	private getStoredOrgCode(): string | null {
		if (typeof window === 'undefined') return null;
		try {
			const userData = secureStorage.getUserData();
			if (userData) {
				// FIX: getUserData() already returns parsed object, no need for JSON.parse()
				return userData.organization_code || null;
			}
		} catch (e) {
			logger.error('Failed to get stored org code', { error: e });
		}
		return null;
	}

	/**
	 * Get stored organization name from secure storage
	 */
	private getStoredOrgName(): string | null {
		if (typeof window === 'undefined') return null;
		try {
			const userData = secureStorage.getUserData();
			if (userData) {
				// FIX: getUserData() already returns parsed object, no need for JSON.parse()
				return userData.organization_name || null;
			}
		} catch (e) {
			logger.error('Failed to get stored org name', { error: e });
		}
		return null;
	}

	/**
	 * Checks if the token stored in sessionStorage is expired based on its expiry date.
	 * Returns true if expired or expiry is invalid, false otherwise.
	 */
	private isTokenExpired(): boolean {
		if (typeof window === 'undefined') return true; // Assume expired on server

		const expiryDate = secureStorage.getTokenExpiry(); // Returns Date | null
		if (!expiryDate) {
			logger.debug('No token expiry found, assuming not expired locally');
			return false; // Assume not expired locally, rely on API check for validity
		}

		try {
			const expiryTime = expiryDate.getTime(); // Get timestamp from Date object
			if (isNaN(expiryTime)) {
				logger.error('Invalid token expiry date in secure storage', { expiryDate });
				return true; // Treat invalid format as expired
			}
			const isExpired = expiryTime < Date.now();
			return isExpired;
		} catch (e) {
			logger.error('Error checking token expiry', { error: e });
			return true; // Treat errors as expired
		}
	}

	/**
	 * Get stored user data from secure storage
	 * Helper method to retrieve organization data when /me endpoint doesn't return it
	 */
	private getStoredUserData(): UserProfile | null {
		if (typeof window === 'undefined') return null;

		try {
			const userData = secureStorage.getUserData();
			if (userData) {
				// FIX: getUserData() already returns parsed object, no need for JSON.parse()
				logger.debug('Retrieved stored user data', {
					email: userData.email,
					organizationCode: userData.organization_code
				});
				return userData as UserProfile;
			}
		} catch (e) {
			logger.error('Failed to get stored user data', { error: e });
		}

		return null;
	}

	/**
	 * Request password reset for a user
	 * @param email The email address to send reset link to
	 * @returns Promise resolving to success status
	 */
	async requestPasswordReset(email: string): Promise<{ success: boolean; message?: string }> {
		try {
			logger.info('Requesting password reset', { email });
			const response = await apiClient.post<{ message: string }>(
				API_CONFIG.AUTH.PASSWORD_RESET_REQUEST(),
				{ email }
			);
			return { success: true, message: response.message || 'Password reset email sent' };
		} catch (error) {
			logger.error('Password reset request failed', { error, email });
			if (error instanceof ApiError) {
				return {
					success: false,
					message: error.toUserFriendlyMessage()
				};
			}
			return {
				success: false,
				message: 'Failed to send password reset email. Please try again.'
			};
		}
	}

	/**
	 * Validate password reset token
	 * @param token The reset token from email
	 * @returns Promise resolving to validation result
	 */
	async validatePasswordResetToken(
		token: string
	): Promise<{ valid: boolean; email?: string; message?: string }> {
		try {
			logger.debug('Validating password reset token');
			const response = await apiClient.post<{ valid: boolean; email?: string }>(
				API_CONFIG.AUTH.PASSWORD_RESET_VALIDATE(),
				{ token }
			);
			return { valid: response.valid, email: response.email };
		} catch (error) {
			logger.error('Password reset token validation failed', { error });
			if (error instanceof ApiError) {
				return {
					valid: false,
					message: error.toUserFriendlyMessage()
				};
			}
			return {
				valid: false,
				message: 'Invalid or expired reset token.'
			};
		}
	}

	/**
	 * Confirm password reset with new password
	 * @param token The reset token from email
	 * @param newPassword The new password to set
	 * @returns Promise resolving to success status
	 */
	async confirmPasswordReset(
		token: string,
		newPassword: string
	): Promise<{ success: boolean; message?: string }> {
		try {
			logger.info('Confirming password reset');
			const response = await apiClient.post<{ message: string }>(
				API_CONFIG.AUTH.PASSWORD_RESET_CONFIRM(),
				{
					token,
					new_password: newPassword
				}
			);
			return { success: true, message: response.message || 'Password reset successful' };
		} catch (error) {
			logger.error('Password reset confirmation failed', { error });
			if (error instanceof ApiError) {
				return {
					success: false,
					message: error.toUserFriendlyMessage()
				};
			}
			return {
				success: false,
				message: 'Failed to reset password. Please try again.'
			};
		}
	}
}

// Create a singleton instance for easy import
export const authApi = new AuthApi();

// Export default for convenience
export default authApi;
